
import { useRef, useEffect, useState } from "react";
import { useChat } from "../../hooks/useChat";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  AlertTitle,
  Fab,
  Backdrop,
  Badge,
} from "@mui/material";
import {
  Send as SendIcon,
  SmartToy as SmartToyIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  Minimize as MinimizeIcon,
} from "@mui/icons-material";
import { Vocabulary } from "../../Utils/Vocabulary";
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import HelpIcon from '@mui/icons-material/Help';
import GavelIcon from '@mui/icons-material/Gavel';
import DomainIcon from '@mui/icons-material/Domain';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
export default function ChatPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { messages, input, handleInputChange, handleSubmit, isLoading, error, setError, chat, setUsedDataset, setMessages } = useChat();
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [showQuickOptions, setShowQuickOptions] = useState(true);
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);
  // Show notification badge when new AI message arrives and chat is closed
  useEffect(() => {
    if (messages.length > 0 && !isOpen) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.content) {
        setHasNewMessage(true);
      }
      setShowQuickOptions(true);
    }
  }, [messages, isOpen]);

  // Clear notification when chat is opened
  useEffect(() => {
    if (isOpen) {
      setHasNewMessage(false);
    }
  }, [isOpen]);

  const showWelcomeMessage = messages.length === 0 && !isLoading && !error;


  const quickOptions = [
    {
      id: "examQuestions",
      label: Vocabulary.questions,
      icon: <HelpIcon sx={{ fontSize: 16 }} />,
      description: Vocabulary.questionDescriptions,
      color: "#505474",
      dataset: "examQuestions",

    },
    {
      id: "rules",
      label: Vocabulary.legalInfo,
      icon: <GavelIcon sx={{ fontSize: 16 }} />,
      description: Vocabulary.legalDescription,
      color: "#505474",
      dataset: "rules",
    },
    {
      id: "legal",
      label: Vocabulary.aboutCompany,
      icon: <DomainIcon sx={{ fontSize: 16 }} />,
      description: Vocabulary.companyDescription,
      color: "#505474",
      dataset: "legal",
    }, {
      id: "client",
      label: Vocabulary.aboutUser,
      icon: <AccountCircleIcon sx={{ fontSize: 16 }} />,
      description: Vocabulary.aboutUserDescription,
      color: "#505474",
      dataset: "client",
    },
  ];
  /**
   * Handles the click event for the quick options buttons.
   * @param {Object} option - The quick option object with id, label, icon, description, color, and dataset properties.
   * @function
   */
  const handleQuickOptionClick = (option: any) => {
    setShowQuickOptions(false);
    const isClientDataset = option.id === "client";
    const clientId = localStorage.getItem("clientId");
    let dataset: any = (chat?.datasets?.find((d: any) => d.name === option.id) || {});
    const sessionId = chat?.history?._id || "";
    if (isClientDataset && clientId) {
      const clientDataset = `client_${clientId}`;
      dataset = chat?.datasets?.find((d: any) => d.name === clientDataset) || {};
    }
    dataset.sessionId = sessionId;

    setUsedDataset(dataset);
  };

  /**
   * Resets the chat modal state.
   * This will close the chat and reset the new message indicator.
   * @function
   */
  const handleReset = () => {
    setShowQuickOptions(true);
    setHasNewMessage(false);
    setError(undefined);
    setMessages([]);
  };

  /**
   * Toggles the chat modal.
   * If the chat is open, it will be closed and vice versa.
   * @function
   */
  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Floating Chat Button */}
      <Badge
        badgeContent={hasNewMessage ? "!" : 0}
        color="error"
        sx={{
          position: "fixed",
          bottom: { xs: 20, sm: 30 },
          right: { xs: 20, sm: 30 },
          zIndex: 1300,
        }}
      >
        <Fab
          color="primary"
          size={isMobile ? "medium" : "large"}
          onClick={toggleChat}
          sx={{
            background: "linear-gradient(45deg, #505474, #B2B0E8)",
            "&:hover": {
              background: "linear-gradient(45deg, #505474, #B2B0E8)",
              transform: "scale(1.1)",
            },
            transition: "all 0.3s ease-in-out",
            animation: hasNewMessage ? "bounce 2s infinite" : "pulse 3s infinite",
            "@keyframes pulse": {
              "0%": { transform: "scale(1)", boxShadow: "0 0 0 0 #B2B0E8" },
              "70%": { transform: "scale(1)", boxShadow: "0 0 0 10px rgba(255, 153, 0, 0)" },
              "100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(255, 153, 0, 0)" },
            },
            "@keyframes bounce": {
              "0%, 20%, 50%, 80%, 100%": { transform: "translateY(0)" },
              "40%": { transform: "translateY(-10px)" },
              "60%": { transform: "translateY(-5px)" },
            },
          }}
        >
          <ChatIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
        </Fab>
      </Badge>

      {/* Backdrop */}
      <Backdrop
        open={isOpen}
        onClick={toggleChat}
        sx={{
          zIndex: 1200,
          backgroundColor: "rgba(0, 0, 0, 0.3)",
        }}
      />

      {/* Chat Interface */}
      <Paper
        elevation={24}
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "fixed",
          bottom: { xs: 0, sm: 20 },
          right: { xs: 0, sm: 20 },
          width: { xs: "100vw", sm: 400, md: 450 },
          height: { xs: "100vh", sm: 600, md: 650 },
          zIndex: 1300,
          display: "flex",
          flexDirection: "column",
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
          transform: isOpen ? "translateY(0) scale(1)" : "translateY(100%) scale(0.95)",
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? "visible" : "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          transformOrigin: "bottom right",
        }}
      >
        {/* Chat Header */}
        <Box
          sx={{
            background: "linear-gradient(45deg,  #505474, #B2B0E8)",
            color: "white",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                {Vocabulary.aiChat}
              </Typography>
              <FiberManualRecordIcon sx={{ color: "#00FF00", fontSize: "10px" }} />
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {isLoading ? Vocabulary.investigating : Vocabulary.online}
              </Typography>
            </Box>
          </Box>
          <Box>
            <IconButton
              onClick={toggleChat}
              sx={{
                color: "white",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              {isMobile ? <CloseIcon /> : <MinimizeIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Messages Area */}
        <Box
          ref={messagesContainerRef}
          sx={{
            flex: 1,
            overflow: "auto",
            bgcolor: "#f8f9fa",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          }}
        >
          <Box
            sx={{
              minHeight: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: showWelcomeMessage ? "center" : "stretch",
              justifyContent: showWelcomeMessage ? "center" : "flex-start",
              p: showWelcomeMessage ? 3 : 0,
            }}
          >
            {/* Welcome Message */}
            {showWelcomeMessage && (
              <Box
                sx={{
                  textAlign: "center",
                  opacity: isOpen ? 1 : 0,
                  transform: isOpen ? "translateY(0)" : "translateY(20px)",
                  transition: "all 0.6s ease-in-out 0.2s",
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Paper
                    elevation={8}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                      background: "linear-gradient(45deg,  #505474, #B2B0E8)",
                    }}
                  >
                    <SmartToyIcon sx={{ color: "white", fontSize: 30 }} />
                  </Paper>
                </Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}>
                  {Vocabulary.chatWelcomeMessage}
                </Typography>
              </Box>
            )}
            {/* Messages */}
            {messages.length > 0 && (
              <Box sx={{ p: 2 }}>
                {messages.map((message: any, index: number) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: 1,
                      mb: 2,
                      justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                      opacity: 0,
                      transform: "translateY(20px)",
                      animation: isOpen ? "slideInUp 0.5s ease-out forwards" : "none",
                      animationDelay: `${index * 0.1 + 0.3}s`,
                      "@keyframes slideInUp": {
                        to: {
                          opacity: 1,
                          transform: "translateY(0)",
                        },
                      },
                    }}
                  >
                    <Paper
                      elevation={1}
                      sx={{
                        px: 2,
                        py: 1.5,
                        maxWidth: "80%",
                        bgcolor: message.role === "user" ? "primary.main" : "white",
                        color: message.role === "user" ? "white" : "text.primary",
                        borderRadius: message.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                        "& .prose": {
                          fontSize: "0.875rem",
                          lineHeight: 1.5,
                          "& p": { margin: 0, marginBottom: "0.5rem" },
                          "& p:last-child": { marginBottom: 0 },
                          "& ul, & ol": { marginLeft: "1rem", marginBottom: "0.5rem" },
                          "& li": { marginBottom: "0.25rem" },
                          "& code": {
                            backgroundColor: message.role === "user" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
                            padding: "2px 4px",
                            borderRadius: "4px",
                            fontSize: "0.8em",
                          },
                          "& pre": {
                            backgroundColor: message.role === "user" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                            padding: "0.5rem",
                            borderRadius: "6px",
                            overflow: "auto",
                            fontSize: "0.75em",
                          },
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {message.content}
                      </Typography>
                    </Paper>
                  </Box>
                ))}
              </Box>
            )}
            {/* Quick Options */}
            {showQuickOptions && (
              <Box
                sx={{
                  opacity: isOpen ? 1 : 0,
                  transform: isOpen ? "translateY(0)" : "translateY(20px)",
                  transition: "all 0.8s ease-in-out 0.4s",
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 2, display: "block", textAlign: "center", fontWeight: 600 }}>
                  {Vocabulary.chatQuickOptions}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "center" }}>
                  {chat === undefined || chat?.datasets?.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "red", mb: 2, display: "block", textAlign: "center" }}>
                      {Vocabulary.noDatasetsAvailable}
                    </Typography>
                  ) : (quickOptions.map((option, index) => (
                    <Button
                      key={option.id}
                      variant="outlined"
                      onClick={() => handleQuickOptionClick(option)}
                      startIcon={option.icon}
                      sx={{
                        width: "100%",
                        maxWidth: 280,
                        justifyContent: "flex-start",
                        textAlign: "left",
                        borderColor: option.color,
                        color: option.color,
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        backdropFilter: "blur(10px)",
                        "&:hover": {
                          backgroundColor: option.color,
                          color: "white",
                          transform: "translateY(-2px)",
                          boxShadow: `0 4px 12px ${option.color}40`,
                        },
                        transition: "all 0.3s ease-in-out",
                        borderRadius: "10px",
                        opacity: 0,
                        animation: isOpen ? "slideInLeft 0.5s ease-out forwards" : "none",
                        animationDelay: `${0.6 + index * 0.1}s`,
                        "@keyframes slideInLeft": {
                          from: {
                            opacity: 0,
                            transform: "translateX(-20px)",
                          },
                          to: {
                            opacity: 1,
                            transform: "translateX(0)",
                          },
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8, fontSize: "0.7rem" }}>
                          {option.description}
                        </Typography>
                      </Box>
                    </Button>
                  )))}

                </Box>
              </Box>
            )}

            {/* Loading State */}
            {isLoading && messages.length === 0 && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
                <CircularProgress size={40} sx={{ color: "primary.main" }} />
              </Box>
            )}

            {/* Error State */}
            {error && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, p: 2 }}>
                <Alert
                  severity="error"
                  sx={{ width: "100%" }}
                  action={
                    <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                      <RefreshIcon />
                    </Button>
                  }
                >
                  <AlertTitle>{Vocabulary.unExpectedErrorTryAgain}</AlertTitle>
                </Alert>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            borderTop: 1,
            borderColor: "grey.200",
            p: 2,
            bgcolor: "white",
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <IconButton
              onClick={handleReset}
              disabled={showQuickOptions}
              sx={{
                bgcolor: "#505474",
                color: "white",
                "&:hover": {
                  bgcolor: "primary.dark",
                  transform: "scale(1.05)",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                  color: "grey.500",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <RestartAltIcon />
            </IconButton>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={Vocabulary.typeYourMessage}
              value={input}
              onChange={handleInputChange}
              disabled={isLoading || showQuickOptions}
              size="small"
              multiline
              maxRows={5}
              minRows={1}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "20px",
                  bgcolor: "#f8f9fa",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />
            <IconButton
              type="submit"
              disabled={isLoading || !input.trim()}
              sx={{
                bgcolor: "#505474",
                color: "white",
                "&:hover": {
                  bgcolor: "primary.dark",
                  transform: "scale(1.05)",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                  color: "grey.500",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              {isLoading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </>
  );
}
